import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoFiltersCard from "../../../components/eco-ui/EcoFiltersCard";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import { useGoBack } from "../../../hooks/useGoBack";

import {
  CashflowParams,
  getCashflowApi,
  getDashboardApi,
  listAccountsApi,
  listCategoriesApi,
  listCostCentersApi,
  listStoresApi,
} from "../api/financeiroApi";
import type { FinAccount, FinCashflow, FinCostCenter, FinDashboard, FinStore } from "../api/finTypes";
import CashDashboard, { DashDrill } from "../components/CashDashboard";
import CounterpartyPicker from "../components/CounterpartyPicker";
import { FINANCEIRO_PAGE, FIN_PAYABLES_PAGE, FIN_RECEIVABLES_PAGE } from "../routes";
import { formatCurrency } from "../../dp/utils/format";
import { CategoryOption, ENTRY_ORIGINS, flattenCategories, monthRange, periodLabel, useFinPermissions } from "../utils/fin";
import "../../dp/pages/DpEco.css";
import "./Financeiro.css";

type Granularity = "day" | "month";
type Basis = "due" | "competence";

/** Padrão: do mês atual até dois meses à frente (mensal) ou o mês atual (diário). */
const defaultRange = (granularity: Granularity) =>
  granularity === "day"
    ? monthRange()
    : { from: monthRange().from, to: monthRange(new Date(), 2).to };

const Money = ({ value, tone }: { value: string; tone?: "in" | "out" }) =>
  Number(value) === 0 ? (
    <span className="ecoTable__faint">—</span>
  ) : (
    <span className={`finMoney ${tone ? `finMoney--${tone}` : Number(value) < 0 ? "finMoney--negative" : ""}`}>
      {formatCurrency(value)}
    </span>
  );

/** Filtros do recorte na URL (`?centro=&loja=…`), com o nome do parâmetro de cada um. */
const SCOPE_PARAMS = ["centro", "loja", "categoria", "origem", "forn"] as const;

/**
 * Fluxo de caixa com o painel de contas a pagar e a receber (17/10/2026). Os
 * filtros valem para os dois. O painel olha os títulos (pela data-base escolhida);
 * a tabela é o fluxo: realizado pelas baixas e previsto pelo que está em aberto.
 * Com recorte de títulos (centro de custo, loja…), a tabela não soma saldo de
 * conta: mostra o resultado acumulado do recorte.
 */
export default function CashflowPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(FINANCEIRO_PAGE());
  const { token } = useFinPermissions();
  const [params, setParams] = useSearchParams();

  const granularity: Granularity = params.get("visao") === "dia" ? "day" : "month";
  const basis: Basis = params.get("base") === "competencia" ? "competence" : "due";
  const fallback = defaultRange(granularity);
  const from = params.get("de") || fallback.from;
  const to = params.get("ate") || fallback.to;
  const accountId = params.get("conta") ?? "";
  const costCenterId = params.get("centro") ?? "";
  const storeId = params.get("loja") ?? "";
  const categoryId = params.get("categoria") ?? "";
  const origin = (params.get("origem") ?? "") in ENTRY_ORIGINS ? (params.get("origem") as string) : "";
  const counterpartyId = params.get("forn") ?? "";
  const counterpartyName = params.get("fornNome") ?? "";

  const [accounts, setAccounts] = useState<FinAccount[]>([]);
  const [costCenters, setCostCenters] = useState<FinCostCenter[]>([]);
  const [stores, setStores] = useState<FinStore[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [data, setData] = useState<FinCashflow | null>(null);
  const [dash, setDash] = useState<FinDashboard | null>(null);
  const [loading, setLoading] = useState(false);

  const setParam = (changes: Record<string, string>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
        return next;
      },
      { replace: true }
    );

  useEffect(() => {
    if (!token) return;
    listAccountsApi(token).then(setAccounts).catch(() => setAccounts([]));
    listCostCentersApi(token).then(setCostCenters).catch(() => setCostCenters([]));
    listStoresApi(token).then(setStores).catch(() => setStores([]));
    listCategoriesApi(token)
      .then((tree) => setCategories(flattenCategories(tree)))
      .catch(() => setCategories([]));
  }, [token]);

  const query: CashflowParams | null = useMemo(
    () =>
      from && to && from <= to
        ? {
            from,
            to,
            granularity,
            accountId: accountId || undefined,
            costCenterId: costCenterId || undefined,
            storeId: storeId || undefined,
            categoryId: categoryId || undefined,
            counterpartyId: counterpartyId || undefined,
            source: origin ? ENTRY_ORIGINS[origin].sources.join(",") : undefined,
          }
        : null,
    [from, to, granularity, accountId, costCenterId, storeId, categoryId, counterpartyId, origin]
  );

  useEffect(() => {
    if (!token || !query) return;
    let alive = true;
    setLoading(true);
    Promise.all([getCashflowApi(token, query), getDashboardApi(token, { ...query, basis })])
      .then(([cashflow, dashboard]) => {
        if (!alive) return;
        setData(cashflow);
        setDash(dashboard);
      })
      .catch((error: Error) => alive && toast.error(error.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [token, query, basis]);

  // Lojas: as do centro de custo escolhido, quando ele tem lojas.
  const ccStoreIds = costCenters.find((c) => c.id === costCenterId)?.storeIds ?? [];
  const storeOptions = ccStoreIds.length ? stores.filter((s) => ccStoreIds.includes(s.id)) : stores;

  const drill = (key: DashDrill, id: string) => {
    if (key === "centro") {
      // Trocar o centro limpa a loja que não pertence a ele.
      const allowed = costCenters.find((c) => c.id === id)?.storeIds ?? [];
      setParam({ centro: id, ...(storeId && allowed.length && !allowed.includes(storeId) ? { loja: "" } : {}) });
    } else setParam({ [key]: id });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scopeCount = SCOPE_PARAMS.filter((k) => params.get(k)).length;
  const activeCount = (params.get("de") || params.get("ate") ? 1 : 0) + (accountId ? 1 : 0) + scopeCount + (basis === "competence" ? 1 : 0);
  const summaryParts = [
    `${periodLabel(from)} a ${periodLabel(to)}`,
    costCenterId ? costCenters.find((c) => c.id === costCenterId)?.name : null,
    storeId ? stores.find((s) => s.id === storeId)?.name : null,
    categoryId ? categories.find((c) => c.id === categoryId)?.label : null,
    counterpartyName || null,
    origin ? ENTRY_ORIGINS[origin].label : null,
    accountId ? accounts.find((a) => a.id === accountId)?.name : null,
  ].filter(Boolean);

  const scoped = Boolean(data?.scoped);
  const todayKey = data ? (granularity === "day" ? data.today : data.today.slice(0, 7)) : "";
  const hasOverdue = data && (Number(data.overdue.in) > 0 || Number(data.overdue.out) > 0);

  return (
    <div className="dpePage">
      <main className="dpePage__main">
        <EcoPageHeader
          crumbs={[{ label: "Financeiro", onClick: () => navigate(FINANCEIRO_PAGE()) }, { label: "Fluxo de caixa" }]}
          title="Fluxo de caixa"
          subtitle="Painel de contas a pagar e a receber e o fluxo realizado e previsto."
          onBack={goBack}
          backLabel="Voltar ao Financeiro"
        />

        <div className="finToolbar">
          <EcoSegmented<Granularity>
            label="Visão"
            options={[
              { value: "month", label: "Por mês" },
              { value: "day", label: "Por dia" },
            ]}
            value={granularity}
            onChange={(next) => setParam({ visao: next === "day" ? "dia" : "", de: "", ate: "" })}
          />
          <EcoSegmented<Basis>
            label="Data-base do painel"
            options={[
              { value: "due", label: "Vencimento" },
              { value: "competence", label: "Competência" },
            ]}
            value={basis}
            onChange={(next) => setParam({ base: next === "competence" ? "competencia" : "" })}
          />
        </div>

        <EcoFiltersCard summary={summaryParts.join(" · ")} activeCount={activeCount}>
          <div className="ecoFilters__grid">
            <div className="ecoField">
              <label htmlFor="cf-de">De</label>
              <input
                id="cf-de"
                type="date"
                className="ecoInput ecoInput--sm"
                value={from}
                max={to}
                onChange={(e) => setParam({ de: e.target.value })}
              />
            </div>
            <div className="ecoField">
              <label htmlFor="cf-ate">Até</label>
              <input
                id="cf-ate"
                type="date"
                className="ecoInput ecoInput--sm"
                value={to}
                min={from}
                onChange={(e) => setParam({ ate: e.target.value })}
              />
            </div>
            {costCenters.length > 0 ? (
              <div className="ecoField">
                <label htmlFor="cf-cc">Centro de custo</label>
                <select
                  id="cf-cc"
                  className="ecoSelect ecoSelect--sm"
                  value={costCenterId}
                  onChange={(e) => (e.target.value ? drill("centro", e.target.value) : setParam({ centro: "" }))}
                >
                  <option value="">Todos</option>
                  {costCenters.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} — {c.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            {stores.length > 0 ? (
              <div className="ecoField">
                <label htmlFor="cf-loja">Loja</label>
                <select
                  id="cf-loja"
                  className="ecoSelect ecoSelect--sm"
                  value={storeId}
                  onChange={(e) => setParam({ loja: e.target.value })}
                >
                  <option value="">Todas</option>
                  {storeOptions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <div className="ecoField">
              <label htmlFor="cf-cat">Categoria</label>
              <select
                id="cf-cat"
                className="ecoSelect ecoSelect--sm"
                value={categoryId}
                onChange={(e) => setParam({ categoria: e.target.value })}
              >
                <option value="">Todas</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="ecoField">
              <label htmlFor="cf-forn">Fornecedor ou cliente</label>
              <CounterpartyPicker
                token={token}
                id="cf-forn"
                value={counterpartyId ? { id: counterpartyId, name: counterpartyName } : null}
                onChange={(v) => setParam({ forn: v?.id ?? "", fornNome: v?.name ?? "" })}
              />
            </div>
            <div className="ecoField">
              <label htmlFor="cf-orig">Origem</label>
              <select
                id="cf-orig"
                className="ecoSelect ecoSelect--sm"
                value={origin}
                onChange={(e) => setParam({ origem: e.target.value })}
              >
                <option value="">Todas</option>
                {Object.entries(ENTRY_ORIGINS).map(([key, o]) => (
                  <option key={key} value={key}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            {accounts.length > 1 ? (
              <div className="ecoField">
                <label htmlFor="cf-conta">Conta</label>
                <select
                  id="cf-conta"
                  className="ecoSelect ecoSelect--sm"
                  value={accountId}
                  onChange={(e) => setParam({ conta: e.target.value })}
                >
                  <option value="">Todas as contas</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>
          {accountId ? (
            <p className="finDesc__sub" style={{ margin: 0 }}>
              Com uma conta escolhida, o previsto considera os títulos com essa conta sugerida.
            </p>
          ) : null}
          {activeCount > 0 ? (
            <div className="ecoFilters__row">
              <button
                type="button"
                className="ecoBtn ecoFilters__clear"
                onClick={() => setParams(granularity === "day" ? { visao: "dia" } : {}, { replace: true })}
              >
                Limpar
              </button>
            </div>
          ) : null}
        </EcoFiltersCard>

        {dash ? (
          <div className={loading ? "finDash--loading" : ""}>
            <CashDashboard data={dash} onDrill={drill} />
          </div>
        ) : loading ? (
          <p className="finDash__empty">Carregando...</p>
        ) : null}

        <header className="finDash__section">
          <h2>Fluxo de caixa</h2>
          <p>
            Realizado pelas baixas; previsto pelo que está em aberto, no vencimento.
            {scoped ? " Com os filtros de títulos não há saldo de conta: o saldo mostra o resultado acumulado do recorte." : ""}
          </p>
        </header>

        {data ? (
          <div className="finSummary">
            {!scoped ? (
              <div className="finStat">
                <span className="finStat__label">Saldo inicial</span>
                <span className={`finStat__value ${Number(data.openingBalance) < 0 ? "finMoney--negative" : ""}`}>
                  {formatCurrency(data.openingBalance)}
                </span>
                <span className="finStat__hint">no dia anterior ao período</span>
              </div>
            ) : null}
            <div className="finStat">
              <span className="finStat__label">Realizado</span>
              <span className="finStat__value">
                {formatCurrency(Number(data.totals.realizedIn) - Number(data.totals.realizedOut))}
              </span>
              <span className="finStat__hint">
                entradas {formatCurrency(data.totals.realizedIn)} · saídas {formatCurrency(data.totals.realizedOut)}
              </span>
            </div>
            <div className="finStat">
              <span className="finStat__label">Previsto</span>
              <span className="finStat__value">
                {formatCurrency(Number(data.totals.forecastIn) - Number(data.totals.forecastOut))}
              </span>
              <span className="finStat__hint">
                a receber {formatCurrency(data.totals.forecastIn)} · a pagar {formatCurrency(data.totals.forecastOut)}
              </span>
            </div>
            <div className={`finStat ${Number(data.closingBalance) < 0 ? "finStat--bad" : ""}`}>
              <span className="finStat__label">{scoped ? "Resultado projetado" : "Saldo projetado"}</span>
              <span className="finStat__value">{formatCurrency(data.closingBalance)}</span>
              <span className="finStat__hint">no fim do período</span>
            </div>
          </div>
        ) : null}

        {hasOverdue && data ? (
          <div className="dpeAlert dpeAlert--calm">
            <span className="dpeAlert__dot dpeAlert__dot--warn" aria-hidden="true" />
            <span className="dpeAlert__text">
              <span className="dpeAlert__title">Atrasados fora do período</span>
              <span className="dpeAlert__desc">
                Vencidos antes de {periodLabel(from)} e ainda em aberto: a pagar{" "}
                <button
                  type="button"
                  className="ecoLinkBtn"
                  onClick={() => navigate(`${FIN_PAYABLES_PAGE()}?situacao=atrasados&periodo=todos`)}
                >
                  {formatCurrency(data.overdue.out)}
                </button>
                , a receber{" "}
                <button
                  type="button"
                  className="ecoLinkBtn"
                  onClick={() => navigate(`${FIN_RECEIVABLES_PAGE()}?situacao=atrasados&periodo=todos`)}
                >
                  {formatCurrency(data.overdue.in)}
                </button>
                . Não entram no saldo projetado.
              </span>
            </span>
          </div>
        ) : null}

        <section className="ecoCard">
          <div className="ecoTableWrap">
            <table className="ecoTable finTable finCashTable">
              <thead>
                <tr>
                  <th>{granularity === "day" ? "Dia" : "Mês"}</th>
                  <th>Entradas</th>
                  <th>Saídas</th>
                  <th>A receber</th>
                  <th>A pagar</th>
                  <th>{scoped ? "Resultado realizado" : "Saldo realizado"}</th>
                  <th>{scoped ? "Resultado projetado" : "Saldo projetado"}</th>
                </tr>
              </thead>
              <tbody>
                {loading && !data ? (
                  <tr>
                    <td colSpan={7} className="ecoEmpty">
                      Carregando...
                    </td>
                  </tr>
                ) : !data || data.buckets.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="ecoEmpty">
                      Escolha um período.
                    </td>
                  </tr>
                ) : (
                  data.buckets.map((b) => (
                    <tr key={b.period} className={b.period === todayKey ? "is-today" : ""}>
                      <td>
                        <strong>{periodLabel(b.period)}</strong>
                      </td>
                      <td>
                        <Money value={b.realizedIn} tone="in" />
                      </td>
                      <td>
                        <Money value={b.realizedOut} tone="out" />
                      </td>
                      <td>
                        <Money value={b.forecastIn} tone="in" />
                      </td>
                      <td>
                        <Money value={b.forecastOut} tone="out" />
                      </td>
                      <td className="finMoney">{formatCurrency(b.realizedBalance)}</td>
                      <td className={`finMoney ${Number(b.balance) < 0 ? "finMoney--negative" : ""}`}>
                        <strong>{formatCurrency(b.balance)}</strong>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
